import React from "react";
import equal from "deep-equal";
import { isNumber, throttle, get, clamp } from "lodash";
import Bezier from "bezier-easing";
import AnimatorView from "./AnimatorView";
import Range from "../../../../utilities/transcriptLib/Range";
import compareRangeLists from "../../../../utilities/transcriptLib/compareRangeLists";
import connectToScroller from "../connectToScroller";
import connectToTranscript from "../connectToTranscript";
import PropsToStateComponent from "../../../../utilities/transcriptLib/PropsToStateComponent";
import { TYPE_ARRAY } from "../../../../utilities/transcriptLib/constants";

const SCROLL_INTERVAL = 1200;

const easing = Bezier(0, 0.5, 0.5, 1);
/**
 * returns true if the difference between the numbers are greater than the delta
 * @param time {Number?}
 * @param base {Number?}
 * @param delta {float} HOW different two numbers have to be.
 * @returns {boolean}
 */
const timeIsSignificantlyDifferent = (time, base, delta = 0.2) => {
  if (time === base) return false;
  if (!(isNumber(time) && isNumber(base))) {
    return time !== base;
  }
  return Math.abs(time - base) > delta;
};

class AnimatorContainer extends PropsToStateComponent {
  constructor(props) {
    super(props);
    this.state = this.propsToState(props);
  }

  componentDidMount() {
    this.throttledUpdateState = throttle(() => {
      this.updateState();
    }, 100);
  }

  updateState() {
    const oldTurnFrame = get(this.state, "turnFrame", -1);
    const newState = this.propsToState();
    // setState callback checks current scroll position and desired scroll position,
    // rangeBrackets, and if skip prev/next button clicked
    this.setState(newState, () => {
      if (
        (newState.turnFrame !== oldTurnFrame && newState.turnFrame > -1) ||
        (this.props.scrollRef.scrollTop !== this._desiredScrollY &&
          (newState.identifiedRangeBrackets.length > 0 ||
            this.props.skipPrevNextHit))
      ) {
        this.scrollTranscript();
      }
    });
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Force animator scroller component to re-render when skipping to next/previous
    // hit. This is needed for single instance of keyword to scroll back to it if
    // user scrolls to another part of the transcript.
    if (nextProps.skipPrevNextHit) {
      return true;
    }

    if (
      timeIsSignificantlyDifferent(
        this.state.currentTime,
        nextState.currentTime
      ) ||
      timeIsSignificantlyDifferent(
        this.state.currentTime,
        nextProps.currentTime
      )
    ) {
      return true;
    }
    if (
      !compareRangeLists(
        this.state.transcriptTimeRanges,
        nextState.transcriptTimeRanges
      ) ||
      !compareRangeLists(
        this.state.transcriptTimeRanges,
        nextProps.transcriptTimeRanges
      )
    ) {
      return true;
    }
    if (
      !equal(
        this.state.identifiedRangeBrackets,
        nextState.identifiedRangeBrackets
      ) ||
      !equal(
        this.state.identifiedRangeBrackets,
        nextProps.identifiedRangeBrackets
      )
    ) {
      return true;
    }

    if (this.state.currentPosition !== nextProps.currentPosition) {
      return true;
    }

    return false;
  }

  componentDidUpdate() {
    if (!equal(this.state, this.propsToState())) {
      if (this.throttledUpdateState) {
        this.throttledUpdateState();
      } else {
        this.updateState();
      }
    }
  }

  getFrame(pProps, ifNotFound = -1) {
    const props = pProps || this.props;
    const {
      currentTime,
      currentPosition,
      transcriptTimeRanges,
      paused,
      onSeek,
    } = props;
    if (!isNumber(currentTime) || !transcriptTimeRanges) {
      return -1;
    }

    // Highlight words by word timestamp as they are spoken for media types with a player (speech)
    if (!currentPosition || !paused) {
      const { nearest, found } = Range.nearestRange(
        transcriptTimeRanges,
        currentTime
      );
      return nearest?.name;
    }

    // Otherwise highlight words by word position when clicked or searched
    const nearest = transcriptTimeRanges.find(
      (i) => i.name === currentPosition
    );
    return nearest?.name || currentPosition;
  }

  /**
   * determines which turn a particular time intersects with.
   * @param pProps {Object}? optional represents the props to calculate for.
   * @param ifNotFound {int}? optional, what to return if a time block is not found.
   * @returns {*}
   */
  getTurnFrame(pProps, ifNotFound = -1) {
    const { transcriptsBySpeaker, currentTime } = pProps || this.props;
    if (!(transcriptsBySpeaker && transcriptsBySpeaker.type === TYPE_ARRAY))
      return ifNotFound;
    const time = currentTime;
    let speakerStartFrame = ifNotFound;
    for (let i = 0; i < transcriptsBySpeaker.value.length; i += 1) {
      const speaker = transcriptsBySpeaker.value[i];
      if (speaker && speaker.has("speaker")) {
        if (speaker.type === TYPE_ARRAY) {
          const ps = speaker.getAll("p");
          if (ps && ps instanceof Set && ps.size > 0) {
            speakerStartFrame = _(Array.from(ps.values()))
              .filter((n) => isNumber(n))
              .min();
          }
        }
        if (speaker.containsTime(time) || speaker.start >= time) {
          return speakerStartFrame;
        }
      }
    }
    return speakerStartFrame;
  }

  propsToState(pProps) {
    const props = pProps || this.props;
    const out = {
      transcriptTimeRanges: props.transcriptTimeRanges,
      transcriptsBySpeaker: props.transcriptsBySpeaker,
      identifiedRangeBrackets: props.identifiedRangeBrackets,
      scrollHeight: props.scrollHeight,
      currentTime: get(props, "currentTime", 0),
      frame: this.getFrame(props),
      turnFrame: this.getTurnFrame(props),
      currentPosition: props.currentPosition,
    };
    return out;
  }

  /**
   * this is a property hook to the scrollRef's scroll Y value
   * @returns {*|number}
   */

  get transcriptScrollY() {
    return get(this.props, "scrollRef.scrollTop") || 0;
  }

  set transcriptScrollY(n) {
    if (!isNumber(n)) {
      return;
    }
    if (this.props.scrollRef) {
      this.props.scrollRef.scrollTop = n;
    }
  }

  /**
   * this is a "fly by wire" property that initiates an async transition from its
   * previous value to its current one.
   *
   * note - the value of this property is what we expect the scroll to be EVENTUALLY.
   * @returns {*|number}
   */
  get desiredScrollY() {
    return this._desiredScrollY || 0;
  }

  set desiredScrollY(newY) {
    this._desiredScrollY = newY;

    if (newY !== this.transcriptScrollY) {
      this._startScrolling(newY);
    }
  }

  /**
   * the distance between the scrollY of the transcript before the scrolling started
   * and the desired scrollY of the transcript
   * @returns {float}
   * @private
   */
  get _transitionSize() {
    if (!this.state.transition) return 0;
    const { startY, newY } = this.state.transition;
    if (!(isNumber(startY) && isNumber(newY))) return 0;
    return newY - startY;
  }

  /**
   * The percent complete (linear, 0..1) that the scrolling shouold be, between the start
   * of the transition and that time + SCROLL_INTERVAL
   * @returns {number}
   * @private
   */
  get _transitionPercentComplete() {
    if (this.transitionHasExpired(true)) return 1;
    const startTime = get(this.state, "transition.start");
    if (!isNumber(startTime)) {
      return 1;
    }

    return clamp((Date.now() - startTime) / SCROLL_INTERVAL, 0, 1);
  }

  get _transitionStartY() {
    return get(this.state, "transition.startY", 0);
  }

  /**
   * reset state to the non-transitioning state and if
   * necessary force the scroll to the desired endpoint
   *
   * @param why
   * @private
   */
  _completeScrolling(why) {
    if (this.desiredScrollY !== this.transcriptScrollY) {
      this.transcriptScrollY = this.desiredScrollY;
    }
    this.setState({
      transition: false,
      scrolling: false,
    });
    this.props.onSkipPrevNextHit(false);
  }

  /**
   * scrolls the transcript by a (hopefully) small amount based on
   * a smoothed interpolation of time and desired distance.
   * @returns {boolean}
   * @private
   */
  _updateScroll() {
    if (this.transitionHasExpired(true)) {
      this._completeScrolling(
        "_updateScroll: expired time",
        this.state.transition
      );
      return false;
    }

    if (this.desiredScrollY === this.transcriptScrollY) {
      this._completeScrolling(
        "_updateScroll: already there",
        this.state.transition
      );
      return false;
    }

    const { _transitionSize, _transitionPercentComplete, _transitionStartY } =
      this;

    if (
      !(
        isNumber(_transitionPercentComplete) &&
        isNumber(_transitionStartY) &&
        isNumber(_transitionPercentComplete)
      )
    ) {
      this._completeScrolling("non-numeric values");
      return false;
    }

    const delta = easing(_transitionPercentComplete) * _transitionSize;
    this.transcriptScrollY = Math.round(_transitionStartY + delta);
    return true;
  }

  /**
   * boolean - true if there is no transition
   * or if the allotted time to complete
   * the transition has expired.
   *
   * @returns {boolean}
   */
  transitionHasExpired() {
    if (!this.state.transition) return true;
    const startTime = get(this.state, "transition.start");
    const endTime = startTime + SCROLL_INTERVAL;
    const now = Date.now();
    if (!startTime || !isNumber(startTime)) {
      return false;
    }
    if (endTime < now) {
      return true;
    }

    return false;
  }

  /**
   * start a repeating animation scroll
   * is possible.
   */
  _startScrolling(newY) {
    if (!this.state.scrolling) {
      this.setState(
        {
          scrolling: true,
          transition: {
            startY: this.transcriptScrollY,
            newY,
            start: Date.now(),
          },
        },
        () => {
          const update = () => {
            if (!this.state.transition) {
              this._completeScrolling("no transition");
              return false;
            }
            if (this._updateScroll() && window) {
              window.requestAnimationFrame(update);
            } else {
              this._completeScrolling("because _updteScroll told us to");
            }
          };

          if (window) {
            window.requestAnimationFrame(update);
          } else {
            this._completeScrolling();
          }
        }
      );
    }
  }

  /**
   * initiates a scroll to the Y position of the desired turn.
   */
  scrollTranscript() {
    const { scrollRef, paused } = this.props;
    const turnFrame = this.state.turnFrame;
    const frame = this.state.frame;
    if (!scrollRef || turnFrame < 0) {
      return;
    }

    // the turn Div that contains the transcript
    // word-index-frame is geared for chat/text/msg media due to implementation of turns array
    const turnElement =
      this.props.scrollRef
        .querySelector(`.word-index-${frame}`)
        ?.closest(".transcript-turn") ||
      this.props.scrollRef.querySelector(`.turn-p-start-${turnFrame}`) ||
      this.props.scrollRef.querySelector(`.chat-turn-time-scroll-true`) ||
      this.props.scrollRef.querySelector(`.chat-turn-range-true`);
    if (turnElement) {
      this.desiredScrollY = turnElement.offsetTop;
    }
  }

  render() {
    return <AnimatorView {...this.state} />;
  }
}

export default connectToScroller(connectToTranscript(AnimatorContainer));
